import React, { useState } from 'react';
import { Copy, CheckCircle2, FileText, Download, Sparkles, AlertTriangle, CheckCircle, Info, ChevronDown } from 'lucide-react';
import { analyzeSiteReport } from '../services/reportAnalyzerService';

export const ResultPanel = ({ resultText }) => {
  const [copied, setCopied] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const handleCopy = () => {
    if (!resultText) return;
    navigator.clipboard.writeText(resultText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!resultText) return;
    const blob = new Blob([resultText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Sovereign_Document_Extraction.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAnalyze = async () => {
    if (!resultText) return;
    setIsAnalyzing(true);
    try {
      const result = await analyzeSiteReport(resultText);
      setAiResult(result);
      // درخواست هوشمندانه نوتیفیکیشن پس از دیدن ارزش محصول
      if (window.OneSignalDeferred) {
        window.OneSignalDeferred.push(async function(OneSignal) {
          if (OneSignal.Notifications && OneSignal.Notifications.permission !== 'granted') {
            setTimeout(() => {
              try {
                OneSignal.Slidedown.promptPush();
              } catch(e) { console.log('OneSignal prompt error:', e); }
            }, 2500); // 2.5 ثانیه بعد از نمایش نتیجه AI
          }
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="bg-[#0F1423] border border-white/5 rounded-3xl p-6 shadow-xl flex flex-col h-full min-h-[400px]">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-bold text-white">نتیجه استخراج</h3>
        
        <div className="flex items-center gap-2">
          {resultText && (
            <>
              <button onClick={handleDownload} className="p-2 bg-white/5 hover:bg-white/10 border border-white/5 text-slate-300 rounded-lg transition-colors" title="دانلود فایل TXT">
                <Download className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleCopy} className="px-3 py-1.5 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-400 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5">
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'کپی شد' : 'کپی'}</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 bg-[#090D16] border border-white/5 rounded-2xl p-4 overflow-hidden mb-4 relative">
        {resultText ? (
          <textarea readOnly value={resultText} className="w-full h-full bg-transparent text-xs text-slate-300 leading-relaxed font-mono resize-none focus:outline-none" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 gap-2">
            <FileText className="w-8 h-8 opacity-20" />
            <p className="text-xs">داده‌های استخراج‌شده در اینجا نمایش داده می‌شود.</p>
          </div>
        )}
      </div>

      {/* AI Assistant Section */}
      {!aiResult ? (
        <button 
          onClick={handleAnalyze}
          disabled={!resultText || isAnalyzing}
          className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-bold text-sm ${
            !resultText ? 'bg-[#090D16] border border-white/5 text-slate-500 cursor-not-allowed' : 
            'bg-sky-400 hover:bg-sky-500 text-[#090D16] shadow-lg shadow-sky-400/10 cursor-pointer'
          }`}
        >
          <Sparkles className={`w-4 h-4 ${isAnalyzing ? 'animate-pulse' : ''}`} />
          <span>{isAnalyzing ? 'در حال تحلیل مهندسی سند...' : 'تحلیل هوشمند گزارش (AI)'}</span>
        </button>
      ) : (
        <div className="bg-[#090D16] border border-sky-500/30 rounded-2xl p-4 animate-fade-in-up">
          <div className="flex items-center justify-between mb-3 border-b border-white/5 pb-2">
            <h4 className="text-xs font-bold text-sky-400 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> خروجی دستیار هوشمند
            </h4>
            <button onClick={() => setAiResult(null)} className="text-slate-500 hover:text-white"><ChevronDown className="w-4 h-4"/></button>
          </div>
          
          <div className="space-y-3">
            <div className="text-xs text-slate-300">
              <span className="font-bold text-white">تشخیص عملیات: </span> {aiResult.summary}
            </div>

            {aiResult.metrics.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {aiResult.metrics.map((m, i) => (
                  <div key={i} className={`px-2 py-1 rounded text-[10px] font-bold border flex items-center gap-1 ${
                    m.status === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
                    m.status === 'warning' ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' :
                    'bg-sky-500/10 border-sky-500/20 text-sky-400'
                  }`}>
                    {m.label}: {m.value}
                  </div>
                ))}
              </div>
            )}

            {aiResult.risks.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-rose-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> هشدارهای کارگاهی:</span>
                <ul className="text-[10px] text-slate-400 list-disc list-inside">
                  {aiResult.risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            {aiResult.recommendations.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3"/> پیشنهادات دستیار:</span>
                <ul className="text-[10px] text-slate-400 list-disc list-inside">
                  {aiResult.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
